import type { AncestorGraph } from '../domain/AncestorGraph';
import type { Location } from '../domain/Location';
import type { MigrationArc } from '../domain/MigrationArc';
import type { TimelinePoint } from '../domain/TimelinePoint';
import { AncestorTimelineService, isEvidencePoint } from './AncestorTimelineService';
import { createLogger } from '../utils/logger';

const logger = createLogger('ArcGeneratorService');

/**
 * Collapses common location-string variants before comparing for movement.
 * WikiTree GEDCOM exports produce inconsistent strings for the same place
 * ("Pennsylvania, USA" vs "Pennsylvania, United States" vs "PA, USA").
 * Intentionally simpler than the full LocationNormalizer — just enough to suppress
 * phantom arcs from obvious variants.
 */
function normalizeRaw(raw: string): string {
  return raw
    .toLowerCase()
    .trim()
    .replace(/\s+/g, ' ')
    .replace(/\bunited states\b/g, 'usa')
    .replace(/\bunited kingdom\b/g, 'uk');
}

type PlacedPoint = TimelinePoint & { location: Location };

export class ArcGeneratorService {
  /**
   * One arc per consecutive change of place along each assigned ancestor's timeline
   * (AncestorTimelineService). Points without a place are skipped, so the line stays
   * continuous across missing data. An arc touching a sibling or child birth is
   * evidenceOnly.
   *
   * MVP: only emits arcs for persons assigned to a colored branch.
   * Phase 3 adds gray arcs (20% opacity) for unassigned persons.
   *
   * ArcLayer (P-08) must skip arcs where year === 0 — that sentinel indicates
   * both the departure and arrival points lack a year.
   */
  generate(
    graph: AncestorGraph,
    assignments: Map<string, string>,
    timelines: Map<string, TimelinePoint[]> = new AncestorTimelineService().build(graph),
  ): MigrationArc[] {
    logger.info(`ArcGenerator started — ${assignments.size} assigned persons`);

    const arcs: MigrationArc[] = [];

    for (const person of graph.persons.values()) {
      const branchId = assignments.get(person.id);
      if (branchId === undefined) {
        continue; // MVP: unassigned persons contribute dots only, no arcs
      }

      const points = (timelines.get(person.id) ?? []).filter(
        (p): p is PlacedPoint => p.location !== undefined,
      );

      for (let i = 0; i + 1 < points.length; i++) {
        const from = points[i] as PlacedPoint;
        const to = points[i + 1] as PlacedPoint;

        if (normalizeRaw(from.location.raw) === normalizeRaw(to.location.raw)) {
          continue; // no movement
        }

        // Use destination year; fall back to departure year; sentinel 0 if both absent.
        const year: number = to.year ?? from.year ?? 0;

        arcs.push({
          from: from.location,
          to: to.location,
          person,
          year,
          branchId,
          // Set to true when either endpoint is evidence; omit (undefined) when both are direct.
          evidenceOnly: isEvidencePoint(from) || isEvidencePoint(to) || undefined,
          fromKind: from.kind,
          toKind: to.kind,
          fromYear: from.year,
          toYear: to.year,
        });
      }
    }

    arcs.sort((a, b) => a.year - b.year);

    const evidenceCount = arcs.filter((a) => a.evidenceOnly === true).length;
    logger.info(`ArcGenerator complete — ${arcs.length} arcs (${evidenceCount} evidence-only)`);

    return arcs;
  }
}
